import {gsap} from "../../libs/gsap/all.js";
import * as PIXI from "pixi.js";
import 'pixi.js/math-extras';
import {PlayerTile} from "./PlayerTile";

// Constructor
export class Player extends PIXI.Container {

  constructor() {
    super();

    this.selectTiles = {};
    this.currentTile = app.game.tiles[app.game.tiles.length - 1][4]; // always start at the same place
    this.targetTile = this.currentTile;
    this.currentDirection = 'center';

    this.playerMovementDuration = 1500; // ms

    let baseYOffset = -4;
    this.initialPosition = {x: this.currentTile.position.x + (this.currentTile.width / 2), y: this.currentTile.position.y + (this.currentTile.height / 2) + baseYOffset};

    this.dimension = {width: 286, height: 156};
    this.warningSpritePosOffset = {x: 65, y: -150};
    this.build();
  }

  build() {
    this.tilesContainer = app.game.playerSelectTiles;
    this.selectTiles = this.tilesContainer.selectTiles;

    this.playerSprite = new PIXI.Sprite(app.game.assets["player-center"]);
    this.playerSprite.position.set(0, 0);
    this.playerSprite.anchor.set(0.5);

    this.warningSprite = new PIXI.Sprite(app.game.assets["collision-warning"]);
    this.warningSprite.position.set(65, -150);
    this.warningSprite.scale.set(0);
    this.warningSprite.anchor.set(0.5);

    this.position.set(this.initialPosition.x, this.initialPosition.y);

    this.addChild(this.playerSprite, this.warningSprite);
  }

  setDirection(direction = this.currentDirection, silent = false, force = false) {
    this.currentDirection = direction; // this is always set in case the direction of the pysical harpon changed while we are animating. It will be updated once anim ended

    // Don't continue with animation if we are either not in game or doing an action already
    if (!app.game.actionsEnabled() && !force) {
      return;
    }

    if (!silent) {
      app.audio.play(app.data.global.sounds.select_tile, 1.0, true);
    }

    // update the select tiles state
    this.updateSelectTilesStatus();

    // save the new target tile
    if (direction == "center") {
      this.targetTile = app.game.getTile(this.currentTile.rowIndex - 1, this.currentTile.colIndex);
    } else {
      if (this.currentTile.colIndex % 2 != 0) {
        // if we are on an odd tile, we can move left, up or right
        if (direction == "left") {
          this.targetTile = app.game.getTile(this.currentTile.rowIndex, this.currentTile.colIndex - 1);
        } else if (direction == "right") {
          this.targetTile = app.game.getTile(this.currentTile.rowIndex, this.currentTile.colIndex + 1);
        }
      } else {
        // if we are on an even tile, we can move left-up, up or right-up
        if (direction == "left") {
          this.targetTile = app.game.getTile(this.currentTile.rowIndex - 1, this.currentTile.colIndex - 1);
        } else if (direction == "right") {
          this.targetTile = app.game.getTile(this.currentTile.rowIndex - 1, this.currentTile.colIndex + 1);
        }
      }
    }

    this.playerSprite.texture = app.game.assets["player-" + direction];

  }

  move() {

    if (!app.game.actionsEnabled()) {
      return;
    }

    if (typeof this.targetTile === "undefined" || this.targetTile.isDisabled) {
      app.audio.play(app.data.global.sounds.blocked_tile, 1.0, true);
      return;
    }

    app.game.lockPlayerActions();

    let targetPos = {x: 0, y: 0};

    // Let's move to the target tile
    // We'll use hardcoded values because there is really no need for it to be dynamic as everything is fixed anyway
    switch (this.currentDirection) {
      case "center":
        targetPos.y -= 160;
        break;
      case "left":
        targetPos.y -= 80;
        targetPos.x -= 235;
        break;
      case "right":
        targetPos.y -= 80;
        targetPos.x += 235;
        break;
    }

    this.hideSelectTiles();

    app.audio.play(app.data.global.sounds.player_move, 1.0, true);

    // Check to see if we got a valid tile or not and act accordingly
    let isValidMove = this.checkTileSuccess();

    if (!isValidMove) {
      // play the hit sound immediately
      this.targetTile.hit();
    }

    gsap.to(this.playerSprite, {
      pixi: {
        x: targetPos.x,
        y: targetPos.y
      },
      duration: this.playerMovementDuration / 1000,
      ease: "power1.inOut",
      onComplete: () => {

        if (isValidMove) {
          this.currentTile = this.targetTile;

          // progress and reset states
          let newPosition = {
            x: this.position.x + this.playerSprite.position.x,
            y: this.position.y + this.playerSprite.position.y
          }
          this.position.set(newPosition.x, newPosition.y); // move container to player position
          this.playerSprite.position.set(0, 0); // move player back to 0

          const reachedEnd = app.game.endOfTurnAction();

          // If we did not reach the end, let's continue the game
          if (!reachedEnd) {
            setTimeout(() => {
              app.game.unlockPlayerActions();
              this.updateTilesStatus(); // enable/disable tiles if they are out of bounds
              this.setDirection(this.currentDirection, true); // update orientation and highlight

              if (app.game.currentState === "instructions-step-3") {
                app.game.closeInstructions();
              }
            }, 100);
          } else {
            // End game
            app.game.endGame();
            app.animateOutGame(5);
          }
        } else {
          this.error();
        }
      }
    })
  }

  error() {
    // Warning animation, handle life loss, animated back player

    this.warningSprite.position.set(this.playerSprite.position.x + this.warningSpritePosOffset.x, this.playerSprite.position.y + this.warningSpritePosOffset.y);

    let errorTimeline = gsap.timeline();

    errorTimeline.fromTo(this.warningSprite, {
        pixi: {
          scale: 0
        }
      },
      {
        pixi: {
          scale: 1
        },
        duration: 0.25,
        ease: "power1.out",
      });

    errorTimeline.fromTo(app.game.errorOverlay, {
        pixi: {
          alpha: 0
        }
      },
      {
        pixi: {
          alpha: 1
        },
        duration: 0.35,
        ease: "power1.inOut",
        repeat: 3,
        yoyo: true,
      }, 0)

    // only lose life if we are in the actual game and not the tutorial
    let shouldEndGame = app.game.loseLife();

    if (app.game.currentState === "game") {
      errorTimeline.add(() => {
        app.game.updateLives();
      }, 0);
    }

    errorTimeline.fromTo(this.playerSprite, {
        pixi: {
          alpha: 1
        }
      },
      {
        pixi: {
          alpha: 0.5
        },
        duration: 0.35,
        ease: "power1.inOut",
        repeat: 3,
        yoyo: true,
      }, 0)

    errorTimeline.to(this.warningSprite, {
      pixi: {
        scale: 0
      },
      duration: 0.25,
      ease: "power1.out",
    });

    if (shouldEndGame) {
      errorTimeline.add(() => {
        this.targetTile.endGame();
      });
    } else {
      // Continue, let's replace the player
      errorTimeline.to(this.playerSprite, {
        pixi: {
          x: 0,
          y: 0
        },
        duration: 0.5,
        ease: "power1.inOut",
      }, "<=")

      errorTimeline.add(() => {
        app.game.unlockPlayerActions();
        this.setDirection(this.currentDirection, true); // update orientation and highlight
        this.updateTilesStatus(); // enable/disable tiles if they are out of bounds
      });
    }
  }

  checkTileSuccess() {
    return app.game.validateTile();
  }

  updateSelectTilesStatus() {
    for (const [tileCode, tile] of Object.entries(this.selectTiles)) {
      // console.log(tileCode, direction);
      tile.highlight(tileCode === this.currentDirection);
    }
  }

  updateTilesStatus() {

    this.tilesContainer.position = this.position;

    for (const [tileCode, tile] of Object.entries(this.selectTiles)) {
      if (tileCode == "left") {
        tile.changeState(this.currentTile.colIndex > 0);
      } else if (tileCode == "right") {
        tile.changeState(this.currentTile.colIndex < app.game.gridDimensions.cols - 1);
      } else {
        tile.changeState(true);
      }
    }
  }

  hideSelectTiles() {
    for (const [tileCode, tile] of Object.entries(this.selectTiles)) {
      tile.changeState(false);
    }
  }

  // Reset the states so we can animate in again and start over
  reset() {
    this.hideSelectTiles();

    this.currentTile = app.game.tiles[app.game.tiles.length - 1][4];
    this.targetTile = this.currentTile;

    this.position.set(this.initialPosition.x, this.initialPosition.y);
    this.playerSprite.position.set(0, 0);
    this.playerSprite.alpha = 1;

    this.setDirection("center", true, true);
  }


}
